import { Command } from "commander";
import chalk from "chalk";
import { analyzeImpact } from "../graph/impactAnalyzer.js";
import type { ImpactReport, ImpactFinding } from "../models/ImpactReport.js";

const RISK_COLOR = { LOW: chalk.green, MEDIUM: chalk.yellow, HIGH: chalk.red };
const SEVERITY_ICON = { error: chalk.red("✖"), warning: chalk.yellow("⚠"), info: chalk.cyan("ℹ") };

function printFindings(label: string, findings: ImpactFinding[]): void {
  if (findings.length === 0) return;
  console.log(chalk.bold(label));
  for (const f of findings) {
    const icon = SEVERITY_ICON[f.severity] ?? chalk.dim("·");
    console.log(`  ${icon} [${f.category}] ${f.message}`);
    if (f.action) console.log(`    ${chalk.dim("→")} ${chalk.dim(f.action)}`);
  }
  console.log();
}

export const impactCommand = new Command("impact")
  .description("Analyze the impact of installing a package into the current project")
  .argument("<package>", "Package name to analyze (e.g. react-native-maps)")
  .option("--json", "Output in JSON format")
  .action(async (packageName: string, options: { json?: boolean }) => {
    const cwd = process.cwd();
    try {
      const report: ImpactReport = await analyzeImpact(cwd, packageName);

      if (options.json) {
        console.log(JSON.stringify(report, null, 2));
        process.exit(0);
        return;
      }

      console.log();
      console.log(chalk.bold.underline(`Impact Analysis: ${packageName}`));
      console.log();

      const riskColor = RISK_COLOR[report.overallRisk] ?? chalk.white;
      console.log(chalk.bold("Overview"));
      console.log(`  Native Module:    ${report.isNative ? chalk.yellow("Yes") : chalk.green("No")}`);
      console.log(`  Has Plugin:       ${report.hasPlugin ? chalk.green("Yes") : chalk.dim("No")}`);
      console.log(`  Overall Risk:     ${riskColor(report.overallRisk)}`);
      console.log();

      if (report.installationSteps.length > 0) {
        console.log(chalk.bold("Installation Steps"));
        report.installationSteps.forEach((step, i) => {
          console.log(`  ${i + 1}. ${step}`);
        });
        console.log();
      }

      if (report.nativeChanges.length > 0) {
        console.log(chalk.bold("Native Changes"));
        for (const change of report.nativeChanges) {
          console.log(`  ${chalk.cyan("·")} ${change}`);
        }
        console.log();
      }

      if (report.buildImplications.length > 0) {
        console.log(chalk.bold("Build Implications"));
        for (const impl of report.buildImplications) {
          console.log(`  ${chalk.yellow("⚠")} ${impl}`);
        }
        console.log();
      }

      if (report.pluginValidations.length > 0) {
        console.log(chalk.bold("Plugin Validations"));
        for (const pv of report.pluginValidations) {
          console.log(`  ${chalk.dim("·")} ${pv}`);
        }
        console.log();
      }

      const errors = report.compatibilityFindings.filter((f) => f.severity === "error");
      const warnings = report.compatibilityFindings.filter((f) => f.severity === "warning");
      const infos = report.compatibilityFindings.filter((f) => f.severity === "info");

      printFindings("Compatibility Issues", [...errors, ...warnings]);
      printFindings("Notes", infos);
    } catch (err: any) {
      if (options.json) {
        console.log(JSON.stringify({ success: false, error: err?.message }, null, 2));
      } else {
        console.log(chalk.red(`✖ ${err?.message || "Unknown error"}`));
      }
      process.exit(1);
    }
  });
